/**
 * ## Primary kill-devil client library
 *
 * When the KD data access layer is configured for the first time and
 * ready for access operations, it will fire a callback with a fully
 * initialized instance of the KD client which itself custom event
 * emitter interface for all socket events, domain data sync events,
 * core service events, and dataservices service events.
 *
 * The client object is intended to have a small interface with a few
 * methods for:
 *
 * - Synchronizing local and remote POS application state
 * - Synchronizing data with POS Core Services
 * - Publishing data to Heartland Commerce Cloud Data Services.
 *
 * Usage:
 *
 * ```javascript
 * const kdclient = require('client');
 *
 * // Initialize the data access layer and configure client components
 * kdclient.ready(config)
 * .then((kd) => {
 *   client_component.configure(kd);
 * });
 * ```
 *
 * @module client/index
 */

const EventEmitter = require("events");

const socket = require("./socket");
const embed = require("./embed");
const Db = require("./db");
const dal = require("./dal");

let kd;

/**
 * KD Client class
 *
 * @typedef KDClient
 * @property {socket.io.Socket} _socket - `Socket.IO` Socket instance
 * @property {function} update - Update or Insert data into the local
 *                             state store via the DAL
 * @property {function} initialize - Emit a sync request to the server for the entire
 *                           object state of a domain collection
 * @property {function} update_ms - Update or Insert Core POS microservice data
 *                                into the local state store via the DAL
 * @property {function} initialize_ms - Emit a sync request to the server over the
 *                              Core POS services room for a specified service
 *                              (e.g. 'print', 'kvs', etc)
 * @property {function} publish_ds - Publish a payload to the dataservices pipeline
 * @property {Object} localstore - Local store used to persist online/offline
 *                               status and cached credentials
 * @property {function} doc - Return a collection of domains, or a specific
 *                          domain object by _id
 * @property {function} find - Find and return domain documents matching a
 *                           single property in the document
 * @property {function} where -  Find and return documents based on the results
 *                            of a function operating over objects in the
 *                            domain collection
 */
class KDClient extends EventEmitter {
  /**
   * Constructor. Creates KDClient instance.
   * @param db - LokiJS instance
   * @param localstore - Localstore instace
   */
  constructor(db, localstore) {
    super();

    this.db = db;
    this.doc = db.doc.bind(db);
    this.find = db.find.bind(db);
    this.where = db.where.bind(db);
    this.whereSort = db.whereSort.bind(db);
    this.replace = db.replace.bind(db);
    this.replaceArray = db.replaceArray.bind(db);
    this.remove = db.remove.bind(db);

    this.localstore = localstore;

    if (localstore.data.credentials.mode === "guestlist") {
      this._socket = socket.configure(this);
    } else if (localstore.data.credentials.mode === "xenial") {
      embed.configure(this);
    }
  }

  /**
   * Factory that creates and configures KDClient instance and
   * instnces necessary for it: Db, Localstore
   * @param {Object} config
   * @returns {Promise} resoles into KDClient instance
   */
  static createClient(config) {
    return Db.createDb(config).then((db) => {
      config.services || (config.services = []);

      if (typeof config.service === "string") {
        if (config.services.indexOf(config.service) < 0) {
          config.services.push(config.service);
        }
      }
      const localstore = new dal.Localstore(config, db);
      kd = new KDClient(db, localstore);
      return kd;
    });
  }

  /**
   * Receive JSON payload from embedded xenial device
   *
   * @param {Object} payload - embed formatted json.
   *
   * @returns {boolean} true - always true
   */

  receive(payload) {
    embed.receive(payload, kd);
    return true;
  }

  /**
   * Send JSON payload to xenial device
   *
   * @param {Object} payload - embed formatted json.
   *
   * @returns {boolean} true - always true
   */

  send(payload) {
    embed.send(payload, kd);
    return true;
  }

  /**
   * Update or insert data
   *
   * @param {Object} proposed - formatted json of the change data.
   * Only include data structure that has changed, all existing data will be
   * maintained and updated against proposed data. Will be checked against
   * validation JSON schema before processing and add credentails.
   *
   * @returns {Object} change object
   */

  update(proposed) {
    if (
      proposed.hasOwnProperty("_kd") &&
      proposed._kd.hasOwnProperty("confirmed")
    ) {
      delete proposed._kd.confirmed;
    }
    return dal
      .update(proposed, this.localstore, this._socket, this.db, kd)
      .then((result) => result)
      .catch((err) => {
        console.log(err, "kdclient.update");
      });
  }

  /**
   * Initialize
   * Gets initialization sync data from the server based on time.
   * sync_request.lastsync is unixtime*1000, (javascript new Data.getTime())
   * from when you want to retrieve all documents from a domain since that time.
   * if you set .lastsync to "0" it will retrieve all documents from that
   * domain
   *
   * @param {Object} syncRequest
   * @param {string} syncRequest.domain
   * @param {string} syncRequest.lastsync - milliseconds since 1970 or "0"
   */
  initialize(syncRequest) {
    return dal.initialize(syncRequest, this.localstore, this._socket, this.db);
  }

  /**
   * Initialize for a microservice
   *
   * Gets all relavent objects from a particular domain
   *
   * @param {Object} syncRequest
   * @param {string} syncRequest.domain
   * @param {string} syncRequest.service - i.e. "print"
   */
  initialize_ms(syncRequest) {
    return dal.initialize_ms(syncRequest, this.localstore, this._socket);
  }

  /**
   * Publish for dataservices over socket
   *
   * @param {Object} data - json object to publish
   * @param {string} data.service - service to publish to i.e. pipeline
   * @param {string} data.system - System format i.e. Dinerware
   * @param {string} data.version - System version
   * @param {string} data.payload - Data to be fed to RabbitMQ.
   *   If JSON, include as property string
   */
  publish_ds(data) {
    return dal.publish_ds(data, this.localstore, this._socket);
  }

  /**
   * Update or insert data for microservice
   * When updates objects from a mircoservice back to the server and then clients,
   * you must pass back the entire object received with the request received on
   * the kd.stream.event (kitchen_display, print, etc) inside the coreservices
   * room. Taking that same object, updating the properties inside the
   * coreservices[0] object will send to back to the server, connected clients,
   * and microservices. The coreservices[0] object must contain .id and .service
   * properties, which match the event being listened to in the client.
   *
   * @param {Object} proposed - change json object
   *
   */
  update_ms(proposed) {
    return dal.update_ms(proposed, this.localstore, this._socket);
  }

  /**
   * Update or insert data for microservice
   * When updates objects from a mircoservice back to the server and then clients,
   * you must pass back the entire object received with the request received on
   * the kd.stream.event (kitchen_display, print, etc) inside the coreservices
   * room. Taking that same object, updating the properties inside the
   * coreservices[0] object will send to back to the server, connected clients,
   * and microservices. The coreservices[0] object must contain .id and .service
   * properties, which match the event being listened to in the client.
   *
   * @param {Object} proposed - change json object
   *
   */
  aux(request) {
    return dal.aux(request, this.localstore, this._socket, this.db);
  }

  rpc(request) {
    return dal.rpc(request, this.localstore, this._socket, this.db);
  }

  getXConfig(options, callback) {
    embed.getXConfig(kd, options, callback);
  }
}

module.exports = KDClient;



/** WEBPACK FOOTER **
 ** ./src/client/index.js
 **/